// ============================================================
// SALT — Analytics Utility
// Located: src/utils/analytics.ts
//
// Conditionally loads Google Analytics (gtag.js) ONLY after
// the user has granted cookie consent. Uses a cross-subdomain
// cookie (sl_cookie_consent) to persist the preference.
//
// Usage:
//   - Call initAnalytics() on app mount (reads existing consent)
//   - Call grantConsent() when user clicks "Accept"
//   - Call revokeConsent() when user clicks "Decline" or changes mind
//   - Call trackPageView() on route changes if needed
// ============================================================

const GA_MEASUREMENT_ID = 'G-XEN94EXNDV';
const CONSENT_COOKIE_NAME = 'sl_cookie_consent';
const CONSENT_MAX_AGE_DAYS = 365;

// ── Cookie helpers (cross-subdomain) ────────────────────────

function getConsentValue(): 'granted' | 'denied' | null {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${CONSENT_COOKIE_NAME}=`));
  if (!match) return null;
  const value = match.split('=')[1];
  if (value === 'granted' || value === 'denied') return value;
  return null;
}

function setConsentCookie(value: 'granted' | 'denied'): void {
  const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
  const isProduction = window.location.hostname.endsWith('playsalt.gg');

  // Match the auth cookie domain strategy:
  // Production: .playsalt.gg (covers all subdomains)
  // Dev: no domain (host-only)
  const domain = isProduction ? '; domain=.playsalt.gg' : '';
  const secure = '; secure';
  const sameSite = isProduction ? '; samesite=lax' : '; samesite=none';

  document.cookie =
    `${CONSENT_COOKIE_NAME}=${value}; max-age=${maxAge}; path=/${domain}${secure}${sameSite}`;
}

// ── GA script loader ────────────────────────────────────────

let gaLoaded = false;

function loadGAScript(): void {
  if (gaLoaded) return;
  gaLoaded = true;

  // 1. Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 2. Initialize dataLayer and gtag (must use `arguments` — not rest params —
  //    because gtag.js expects Arguments objects in the dataLayer, not Arrays)
  (window as any).dataLayer = (window as any).dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  (window as any).gtag = function () { (window as any).dataLayer.push(arguments); };

  (window as any).gtag('js', new Date());
  (window as any).gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,        // GDPR: anonymize IP addresses
    cookie_flags: 'samesite=lax;secure',
  });
}

function removeGAScriptAndCookies(): void {
  // Remove the gtag script tag if present
  const scripts = document.querySelectorAll(
    `script[src*="googletagmanager.com/gtag/js"]`
  );
  scripts.forEach((s) => s.remove());

  // Clear GA cookies (_ga, _ga_*, _gid)
  const gaCookies = document.cookie
    .split('; ')
    .filter((c) => c.startsWith('_ga') || c.startsWith('_gid'));

  const isProduction = window.location.hostname.endsWith('playsalt.gg');
  const domain = isProduction ? '; domain=.playsalt.gg' : '';

  gaCookies.forEach((c) => {
    const name = c.split('=')[0];
    document.cookie = `${name}=; max-age=0; path=/${domain}`;
    // Also try without domain in case it was set differently
    document.cookie = `${name}=; max-age=0; path=/`;
  });

  gaLoaded = false;
  delete (window as any).gtag;
  delete (window as any).dataLayer;
}

// ── Public API ──────────────────────────────────────────────

/**
 * Call on app mount. If user previously granted consent, loads GA.
 * If denied or no choice yet, does nothing.
 */
export function initAnalytics(): void {
  const consent = getConsentValue();
  if (consent === 'granted') {
    loadGAScript();
  }
}

/**
 * User accepted analytics cookies. Persist preference and load GA.
 */
export function grantConsent(): void {
  setConsentCookie('granted');
  loadGAScript();
}

/**
 * User declined analytics cookies. Persist preference and remove GA.
 */
export function revokeConsent(): void {
  setConsentCookie('denied');
  removeGAScriptAndCookies();
}

/**
 * Check if user has made a consent choice (either way).
 */
export function hasConsentChoice(): boolean {
  return getConsentValue() !== null;
}

/**
 * Check if analytics consent was granted.
 */
export function isAnalyticsEnabled(): boolean {
  return getConsentValue() === 'granted';
}

/**
 * Manually track a page view (useful for SPA route changes).
 * No-op if GA isn't loaded.
 */
export function trackPageView(path?: string): void {
  if (!gaLoaded || !(window as any).gtag) return;
  (window as any).gtag('config', GA_MEASUREMENT_ID, {
    page_path: path || window.location.pathname,
  });
}